import React from "react";

import CustomAutoCompleteFilter from "../lib/components/customdatagridfilter/CustomFilter";
import { FormatterNumber, TextFormatter } from "../lib/components/Formatters";
import { handleColumnsArrangement, handleColumnsWidth } from "../lib/helpers/grid";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import WarningIcon from "@material-ui/icons/Warning";
import ErrorIcon from "@material-ui/icons/Error";

const cellWidthSmall = 90;
const cellWidthMedium = 130;

const defaultColumnProperties = {
  resizable: true,
  sortable: true,
  filterable: true,
};

const section = "stockchecksummary";

let currentSpace = true;

const ResultColours = {
  Balanced: { bg: "#e8f5e9", color: "#2e7d32", icon: CheckCircleIcon },
  "Detail Differences": { bg: "#fff3e0", color: "#e65100", icon: WarningIcon },
  "Stock Missing": { bg: "#ffebee", color: "#c62828", icon: ErrorIcon },
};

const ResultFormatter = ({ row }) => {
  const result = row["Result"];
  const config = ResultColours[result] || { bg: "#fff", color: "#000", icon: null };
  const IconComponent = config.icon;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        color: config.color,
        fontWeight: "bold",
        padding: "2px 8px",
        borderRadius: 4,
      }}
    >
      {IconComponent && <IconComponent style={{ fontSize: 16 }} />}
      <span>{result}</span>
    </div>
  );
};

/* zero is the expected value for every difference column, so only draw attention to the rest */
const DifferenceFormatter = ({ row, column }) => {
  const value = row[column.key] || 0;
  return (
    <div
      style={{
        textAlign: "right",
        color: value > 0 ? "#c62828" : "#666",
        fontWeight: value > 0 ? "bold" : "normal",
      }}
    >
      {value.toLocaleString()}
    </div>
  );
};

const MatchedFormatter = ({ row, column }) => {
  const value = row[column.key] || 0;
  const total = row["Total Pallets"] || 0;
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;

  return (
    <div style={{ textAlign: "right", color: "#2e7d32", fontWeight: value === total ? "bold" : "normal" }}>
      {value.toLocaleString()} <span style={{ fontSize: 11, color: "#666" }}>({pct}%)</span>
    </div>
  );
};

const FileAgeFormatter = ({ row }) => {
  const days = row["File Age Days"] || 0;
  let color = "#2e7d32"; // green - fresh
  if (days > 7) color = "#e65100"; // orange - getting stale
  if (days > 30) color = "#c62828"; // red - old

  return (
    <div style={{ textAlign: "right", color, fontWeight: days > 7 ? "bold" : "normal" }}>
      {days} {days === 1 ? "day" : "days"}
    </div>
  );
};

export const GridColumns = (data, filters, arrangement, columnsWidth) => {
  let postValueInfo = "";

  if (data) {
    if (currentSpace) {
      postValueInfo = " ";
    }
    currentSpace = !currentSpace;
  }

  const columns = [
    {
      key: "Location Code",
      name: "Location" + postValueInfo,
      width: cellWidthSmall,
      formatter: TextFormatter,
      filterRenderer: (e) => <CustomAutoCompleteFilter {...e} multiSelection={true} currentFilters={filters} fieldname={"Location Code"} data={data} section={section} />,
    },
    {
      key: "Result",
      name: "Status" + postValueInfo,
      width: 180,
      formatter: ResultFormatter,
      filterRenderer: (e) => <CustomAutoCompleteFilter {...e} multiSelection={true} currentFilters={filters} fieldname={"Result"} data={data} section={section} />,
    },
    {
      key: "Total Pallets",
      name: "Pallets" + postValueInfo,
      width: cellWidthSmall,
      formatter: FormatterNumber,
      filterRenderer: (e) => <CustomAutoCompleteFilter {...e} multiSelection={true} currentFilters={filters} fieldname={"Total Pallets"} data={data} section={section} />,
    },
    {
      key: "Matched",
      name: "Matched" + postValueInfo,
      width: cellWidthMedium,
      formatter: MatchedFormatter,
      filterRenderer: (e) => <CustomAutoCompleteFilter {...e} multiSelection={true} currentFilters={filters} fieldname={"Matched"} data={data} section={section} />,
    },
    {
      key: "Mismatched",
      name: "Mismatched" + postValueInfo,
      width: cellWidthSmall,
      formatter: DifferenceFormatter,
      filterRenderer: (e) => <CustomAutoCompleteFilter {...e} multiSelection={true} currentFilters={filters} fieldname={"Mismatched"} data={data} section={section} />,
    },
    {
      key: "Missing On System",
      name: "Missing (System)" + postValueInfo,
      width: cellWidthMedium,
      formatter: DifferenceFormatter,
      filterRenderer: (e) => <CustomAutoCompleteFilter {...e} multiSelection={true} currentFilters={filters} fieldname={"Missing On System"} data={data} section={section} />,
    },
    {
      key: "Missing On File",
      name: "Missing (File)" + postValueInfo,
      width: cellWidthMedium,
      formatter: DifferenceFormatter,
      filterRenderer: (e) => <CustomAutoCompleteFilter {...e} multiSelection={true} currentFilters={filters} fieldname={"Missing On File"} data={data} section={section} />,
    },
    {
      key: "File Age Days",
      name: "File Age" + postValueInfo,
      width: cellWidthSmall,
      formatter: FileAgeFormatter,
      filterRenderer: (e) => <CustomAutoCompleteFilter {...e} multiSelection={true} currentFilters={filters} fieldname={"File Age Days"} data={data} section={section} />,
    },
    {
      key: "filename",
      name: "PS File" + postValueInfo,
      width: cellWidthMedium,
      formatter: TextFormatter,
      filterRenderer: (e) => <CustomAutoCompleteFilter {...e} multiSelection={true} currentFilters={filters} fieldname={"filename"} data={data} section={section} />,
    },
    {
      key: "formattedDate",
      name: "Imported" + postValueInfo,
      width: cellWidthMedium,
      formatter: TextFormatter,
      filterRenderer: (e) => (
        <CustomAutoCompleteFilter {...e} multiSelection={true} currentFilters={filters} fieldname={"formattedDate"} data={data} section={section} orderDescending />
      ),
    },
  ].map((c) => ({ ...c, ...defaultColumnProperties }));

  return handleColumnsWidth(handleColumnsArrangement([], arrangement, columns), columnsWidth, columns);
};
